/**
 * App Distribution — Admin page for publishing new Carloo Tex APK builds.
 *
 * Pattern mirrors the vehicle-hub-286 distribution panel (Feb 22, 2026 user
 * ask). End-user phones get the new build automatically via the existing
 * `AppVersionGuard` once the admin saves a new release here — no manual
 * uninstall / reinstall needed because the APK is re-signed with the same
 * keystore as the previous build (Android treats it as an UPDATE).
 *
 * Backend: routes/app_distribution.py (uses GridFS for blob storage).
 * Public download URL: /api/uploads/carloo-tex.apk (no auth, stable).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, Download, CheckCircle2, AlertTriangle, Trash2, RotateCcw,
  Smartphone, Loader2, FileArchive, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STABLE_PATH = '/api/uploads/carloo-tex.apk';

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n > 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  if (n > 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

export default function AppDistribution() {
  const [releases, setReleases] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadStatus, setUploadStatus] = useState(null);   // { progress, msg }
  const [busyAction, setBusyAction] = useState(null);       // releaseId or 'upload'
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    file: null,
    version_name: '',
    version_code: '',
    release_notes: '',
    mandatory: false,
  });

  const downloadUrl = `${API_URL}${STABLE_PATH}`;

  const loadReleases = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/app-releases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setReleases(data.releases || []);
      setCurrent(data.current || null);
    } catch (e) {
      toast.error(`Could not load releases: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReleases(); }, [loadReleases]);

  const onFileSelect = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.apk')) {
      toast.error('Please select a .apk file');
      return;
    }
    setForm((p) => ({ ...p, file: f }));
  };

  const onSubmitUpload = async (e) => {
    e.preventDefault();
    if (!form.file) {
      toast.error('Choose an APK file first');
      return;
    }
    setBusyAction('upload');
    setUploadStatus({ progress: 0, msg: 'Uploading…' });

    // Use XHR for progress reporting (fetch has no upload progress).
    const fd = new FormData();
    fd.append('file', form.file);
    if (form.version_name) fd.append('version_name', form.version_name);
    if (form.version_code) fd.append('version_code', form.version_code);
    if (form.release_notes) fd.append('release_notes', form.release_notes);
    fd.append('mandatory', form.mandatory ? 'true' : 'false');

    const token = localStorage.getItem('token');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/admin/app-releases`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) {
        const pct = Math.round((evt.loaded / evt.total) * 100);
        setUploadStatus({ progress: pct, msg: `Uploading… ${pct}%` });
      }
    };
    xhr.onload = () => {
      setBusyAction(null);
      setUploadStatus(null);
      if (xhr.status >= 200 && xhr.status < 300) {
        toast.success('Release published. Phones will see the update on next app open.');
        setForm({ file: null, version_name: '', version_code: '', release_notes: '', mandatory: false });
        if (fileInputRef.current) fileInputRef.current.value = '';
        loadReleases();
      } else {
        let msg = `Upload failed (HTTP ${xhr.status})`;
        try {
          const body = JSON.parse(xhr.responseText);
          msg = body.detail || msg;
        } catch { /* ignore parse */ }
        toast.error(msg);
      }
    };
    xhr.onerror = () => {
      setBusyAction(null);
      setUploadStatus(null);
      toast.error('Upload failed — network error');
    };
    xhr.send(fd);
  };

  const importFromCi = async () => {
    if (!window.confirm(
      'Pull the latest APK built by GitHub Actions CI?\n\n' +
      'This fetches the `apk-latest` release asset from your GitHub repo, ' +
      'extracts versionCode + versionName from the AndroidManifest, and ' +
      'publishes it here as the new current release.'
    )) return;
    setBusyAction('ci-import');
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      if (form.release_notes) fd.append('release_notes', form.release_notes);
      fd.append('mandatory', form.mandatory ? 'true' : 'false');
      const r = await fetch(`${API_URL}/api/admin/app-releases/import-from-ci`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      const result = await r.json();
      toast.success(
        `Imported v${result.release.version_name} (build ${result.release.version_code}) from ${result.imported_from.tag}`,
        { duration: 6000 },
      );
      loadReleases();
    } catch (e) {
      toast.error(`CI import failed: ${e.message}`);
    } finally {
      setBusyAction(null);
    }
  };

  const promote = async (id) => {
    if (!window.confirm('Promote this release to current? Phones will start downloading it.')) return;
    setBusyAction(id);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/app-releases/${id}/promote`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success('Release promoted');
      loadReleases();
    } catch (e) {
      toast.error(`Promote failed: ${e.message}`);
    } finally {
      setBusyAction(null);
    }
  };

  const remove = async (id, ver) => {
    if (!window.confirm(`Permanently delete release v${ver}? This cannot be undone.`)) return;
    setBusyAction(id);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/app-releases/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success('Release deleted');
      loadReleases();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setBusyAction(null);
    }
  };

  // QR code via the free goqr.me API (no key, no install). Encodes the
  // stable APK URL — staff scan with phone camera → browser downloads APK.
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(downloadUrl)}`;

  return (
    <div className="space-y-5" data-testid="app-distribution-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Smartphone size={22} /> App Distribution
          </h1>
          <p className="text-sm text-gray-500">
            Upload signed Android APKs. The latest is auto-served at the stable URL{' '}
            <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{STABLE_PATH}</code> — phones receive
            the OTA update on next app open.
          </p>
        </div>
        <button
          onClick={loadReleases}
          disabled={loading}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid="app-dist-refresh"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {/* Current release + QR */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border rounded-xl p-5 shadow-sm" data-testid="current-release-card">
          {current ? (
            <>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Current Release</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900">v{current.version_name}</span>
                    <span className="text-gray-500">build #{current.version_code}</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded font-medium">
                      <CheckCircle2 size={12} /> LIVE
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {fmtBytes(current.size_bytes)} · uploaded {fmtDateTime(current.uploaded_at)} · by {current.uploaded_by || 'Admin'}
                  </div>
                  {current.release_notes && (
                    <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg border">
                      {current.release_notes}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                  data-testid="download-apk-btn"
                >
                  <Download size={16} /> Download APK
                </a>
                <span className="text-xs text-gray-500 break-all">{downloadUrl}</span>
              </div>
              {current.mandatory && (
                <div className="mt-3 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  Mandatory update — older builds are blocked at login until users install this one.
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileArchive size={32} className="mx-auto mb-2 opacity-40" />
              No release published yet. Upload your first signed APK below.
            </div>
          )}
        </div>

        <div className="bg-white border rounded-xl p-5 shadow-sm flex flex-col items-center" data-testid="qr-card">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Install QR</div>
          {current ? (
            <>
              <img src={qrSrc} alt="Install QR" width="220" height="220" className="rounded-lg border" />
              <div className="text-[11px] text-gray-500 mt-2 text-center">Scan with phone camera to download</div>
            </>
          ) : (
            <div className="w-[220px] h-[220px] border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-400 text-center px-4">
              QR will appear once a release is published
            </div>
          )}
        </div>
      </div>

      {/* Upload new release */}
      <form onSubmit={onSubmitUpload} className="bg-white border rounded-xl p-5 shadow-sm space-y-4" data-testid="upload-form">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Upload size={16} className="text-blue-600" />
            <h2 className="font-semibold text-gray-900">Publish a New Release</h2>
          </div>
          <button
            type="button"
            onClick={importFromCi}
            disabled={busyAction !== null}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title="Pull the latest APK built by GitHub Actions and publish it here"
            data-testid="import-from-ci-btn"
          >
            {busyAction === 'ci-import' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Import latest CI build
          </button>
        </div>
        <p className="text-[11px] text-gray-500 -mt-2">
          Two paths: <b>(A)</b> click "Import latest CI build" — pulls the APK GitHub Actions just built ·
          <b> (B)</b> upload a signed APK manually below.
        </p>

        <div>
          <label className="block text-xs text-gray-600 mb-1 font-medium">APK File *</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            onChange={(e) => onFileSelect(e.target.files?.[0])}
            disabled={busyAction === 'upload'}
            className="block w-full text-sm text-gray-700 file:mr-3 file:px-4 file:py-2 file:bg-blue-600 file:text-white file:border-0 file:rounded-lg file:font-medium hover:file:bg-blue-700 file:cursor-pointer disabled:opacity-50"
            data-testid="upload-apk-input"
          />
          {form.file && (
            <p className="text-xs text-gray-600 mt-1">
              Selected: <span className="font-medium">{form.file.name}</span> · {fmtBytes(form.file.size)}
            </p>
          )}
          <p className="text-[11px] text-gray-500 mt-1">
            Version is auto-detected from the APK manifest. Manual fields below are optional fallbacks.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Version Name (auto)</label>
            <input
              type="text"
              placeholder="e.g. 2.2.0"
              value={form.version_name}
              onChange={(e) => setForm({ ...form, version_name: e.target.value })}
              disabled={busyAction === 'upload'}
              className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
              data-testid="upload-version-name"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Version Code (auto)</label>
            <input
              type="number"
              placeholder="e.g. 20200"
              value={form.version_code}
              onChange={(e) => setForm({ ...form, version_code: e.target.value })}
              disabled={busyAction === 'upload'}
              className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
              data-testid="upload-version-code"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Mandatory</label>
            <label className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={form.mandatory}
                onChange={(e) => setForm({ ...form, mandatory: e.target.checked })}
                disabled={busyAction === 'upload'}
                data-testid="upload-mandatory"
              />
              <span>Block older builds at login</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Release Notes (optional)</label>
          <textarea
            rows={3}
            placeholder="What changed in this build (shown on the App Update banner)"
            value={form.release_notes}
            onChange={(e) => setForm({ ...form, release_notes: e.target.value })}
            disabled={busyAction === 'upload'}
            className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
            data-testid="upload-release-notes"
          />
        </div>

        {uploadStatus && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">{uploadStatus.msg}</span>
              <span className="font-medium text-gray-700">{uploadStatus.progress}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-2 transition-all"
                style={{ width: `${uploadStatus.progress}%` }}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={busyAction === 'upload' || !form.file}
          className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm"
          data-testid="upload-submit-btn"
        >
          {busyAction === 'upload' ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          Publish Release
        </button>
      </form>

      {/* History */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden" data-testid="release-history-card">
        <div className="px-5 py-3 border-b bg-gray-50">
          <h2 className="font-semibold text-gray-900 text-sm">Release History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-[11px] uppercase text-gray-500">
                <th className="px-4 py-2 text-left">Version</th>
                <th className="px-4 py-2 text-left">Code</th>
                <th className="px-4 py-2 text-left">Size</th>
                <th className="px-4 py-2 text-left">Uploaded</th>
                <th className="px-4 py-2 text-left">Notes</th>
                <th className="px-4 py-2 text-center">Status</th>
                <th className="px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && releases.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No releases yet</td></tr>
              )}
              {!loading && releases.map((r) => (
                <tr key={r.id} className={r.is_current ? 'bg-emerald-50/40' : ''} data-testid={`release-row-${r.version_code}`}>
                  <td className="px-4 py-2.5 font-semibold text-gray-900">v{r.version_name}</td>
                  <td className="px-4 py-2.5 text-gray-700 tabular-nums">#{r.version_code}</td>
                  <td className="px-4 py-2.5 text-gray-700 tabular-nums">{fmtBytes(r.size_bytes)}</td>
                  <td className="px-4 py-2.5 text-gray-600 text-xs">
                    <div>{fmtDateTime(r.uploaded_at)}</div>
                    <div className="text-gray-400">by {r.uploaded_by || 'Admin'}</div>
                  </td>
                  <td className="px-4 py-2.5 max-w-[320px] text-xs text-gray-700">
                    <div className="line-clamp-2">{r.release_notes || '—'}</div>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {r.is_current ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[11px] rounded font-medium">
                        <CheckCircle2 size={11} /> LIVE
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[11px] rounded">ARCHIVE</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-center gap-1">
                      {!r.is_current && (
                        <button
                          onClick={() => promote(r.id)}
                          disabled={busyAction === r.id}
                          title="Promote to current"
                          className="p-1.5 hover:bg-blue-50 rounded text-blue-600 disabled:opacity-40"
                          data-testid={`promote-${r.version_code}`}
                        >
                          {busyAction === r.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                        </button>
                      )}
                      {!r.is_current && (
                        <button
                          onClick={() => remove(r.id, r.version_name)}
                          disabled={busyAction === r.id}
                          title="Delete"
                          className="p-1.5 hover:bg-rose-50 rounded text-rose-600 disabled:opacity-40"
                          data-testid={`delete-${r.version_code}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
